import {
    forwardRef,
    memo,
    PropsWithChildren,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import dynamic from "next/dynamic";
import {observer} from 'mobx-react';

import BlockWrapper from "./_BlockWrapper";
import useFinalizeBlock from "../../../hooks/useFinalizeBlock";
import {ErrorBoundary} from "../../utilities/ErrorBoundary";
import {useStores} from "../../../stores/useStores";

const blockTypes = {
    'sharedfiles': dynamic(() => import('./SharedFilesBlock/SharedFilesBlock'), {loading: () => <></>}),
    'organization': dynamic(() => import('./OrganizationBlock'), {loading: () => <></>}),
    'school': dynamic(() => import('./OrganizationBlock'), {loading: () => <></>}),
    'district': dynamic(() => import('./OrganizationBlock'), {loading: () => <></>}),
    'classroom': dynamic(() => import('./OrganizationBlock'), {loading: () => <></>}),
    'person': dynamic(() => import("./PersonBlock"), {loading: () => <></>}),
    'department': dynamic(() => import("./GroupBlock/GroupBlock"), {loading: () => <></>}),
    'section': dynamic(() => import("./GroupBlock/GroupBlock"), {loading: () => <></>}),
    'group': dynamic(() => import("./GroupBlock/GroupBlock"), {loading: () => <></>}),
    'team': dynamic(() => import("./GroupBlock/GroupBlock"), {loading: () => <></>}),
    'eventlist': dynamic(() => import("./EventsBlock/EventListBlock"), {loading: () => <></>,  ssr: false}),
    'quicklink': dynamic(() => import("./QuickLinksBlock/QuicklinkBlock"), {loading: () => <></>}),
    'html': dynamic(() => import("./HtmlBlock/HtmlBlock"), {loading: () => <></>}),
    "content_item": dynamic(() => import("./ContentItemBlock/ContentItemBlock"), {loading: () => <></>}),
    'news': dynamic(() => import("./NewsBlock/NewsBlock"), {loading: () => <></>}),
    'message': dynamic(() => import("./MessageBlock/MessageBlock"), {loading: () => <></>}),
    'video': dynamic(() => import("./VideoBlock/VideoBlock"), {loading: () => <></>}),
    'facebook': dynamic(() => import("./SocialBlocks/FacebookBlock"), {loading: () => <></>}),
    'pinterest': dynamic(() => import("./SocialBlocks/PinterestBlock"), {loading: () => <></>}),
    'instagram': dynamic(() => import("./SocialBlocks/InstagramBlock"), {loading: () => <></>}),
    'twitter': dynamic(() => import("./SocialBlocks/TwitterBlock"), {loading: () => <></>}),
    'poll': dynamic(() => import("./PollBlock/PollBlock"), {loading: () => <></>}),
    'page': dynamic(() => import("./PageBlock/PageBlock"), {loading: () => <></>}),
    'staff': dynamic(() => import("./StaffBlock/StaffBlock"), {loading: () => <></>}),
    'weather': dynamic(() => import("./WeatherBlock/WeatherBlock"), {ssr: false}),
    'youtube': dynamic(() => import("./VideoBlock/VideoBlock"), {loading: () => <></>}),
    "loading": dynamic(() => import("./LoadingBlock"), {loading: () => <></>}),
    "error": dynamic(() => import("./ErrorBlock"), {loading: () => <></>}),
    "shop": dynamic(() => import("./ShopBlock/ShopBlock"), {loading: () => <></>}),
};

export type BlockProps = {
    blockObj: AnyBlockObj,
    sizeX: number,
    sizeY: number,
    packeryLayout?: () => void,
    loading?: boolean,
    tickLoadedAsync?: () => void,
    gridType: string,
    isFirst: boolean,
    locksAreEnabled: boolean,
    maxColumns: number,
    normalizedFirstCharacter: string,
    isBlockPreview: boolean,
    hasMicrosite: boolean,
} & ({
    outsideGrid: false,
    rowHeight: number,
    w: number,
    h: number,
    static: boolean,
    x?: number,
    y?: number,
    isResizable: boolean,
    isDraggable: boolean,
    maxW?: number,
    maxH?: number,
} | {
    outsideGrid: true,
    rowHeight?: never,
    w?: never,
    h?: never,
    static?: never,
    x?: never,
    y?: never,
    isResizable?: never,
    isDraggable?: never,
})

// individual blocks should extend this type with blockObj
export interface IBlockComponentProps {
    sizeX: number,
    sizeY: number,
    htmlId: string,
    blockRef: RefObject<HTMLDivElement>,
    setShowExpandButton: SetStateAction<any>,
    outsideGrid: boolean,
    isBlockPreview: boolean,
    blockHovered: boolean,
}

const Block = memo(observer(forwardRef((props: PropsWithChildren<BlockProps>, outerRef: RefObject<any>) => {

    // this is here so that we react to size changes, it's a must so that non-grid blocks work
    const {interfaceStore} = useStores();
    const [showExpandButton, setShowExpandButton] = useState(false);
    const [blockExpanded, setBlockExpanded] = useState(false);
    const blockRef = useRef(null);
    const {blockObj, hasMicrosite, isBlockPreview, outsideGrid, tickLoadedAsync} = props;

    useImperativeHandle(outerRef, () => blockRef.current, []);

    useFinalizeBlock(blockObj, isBlockPreview);
    const htmlId = outsideGrid ? `edit${blockObj.id}` : blockObj.htmlId;

    let blockType: string = blockObj.blockType;
    if ((!blockObj.asyncLoaded && blockObj.loadAsync) || props.loading) {
        blockType = "loading";
    } else if (blockObj.errorMessage && blockObj.errorMessage !== "") {
        blockType = "error";
    }
    const BlockComponent = useCallback(props => {
        const Component = blockTypes[blockType] || "error";
        return <Component {...props} />;
    }, [blockType])

    useEffect(() => {
        if (blockObj.asyncLoaded) {
            if (tickLoadedAsync) tickLoadedAsync()
            blockType = blockObj.blockType;
        }
    }, [blockObj.asyncLoaded])

    return (
        <ErrorBoundary>
            <BlockWrapper
                {...props}
                sizeX={props.sizeX}
                sizeY={props.sizeY}
                gridPosX={props.x}
                gridPosY={props.y}
                locksAreEnabled={props.locksAreEnabled}
                maxColumns={props.maxColumns}
                normalizedFirstCharacter={props.normalizedFirstCharacter}
                blockRef={blockRef}
                ref={outerRef}
                blockObj={blockObj}
                htmlId={htmlId}
                blockExpanded={blockExpanded}
                setBlockExpanded={setBlockExpanded}
                showExpandButton={showExpandButton}
                hasMicrosite={hasMicrosite}
                gridLocked={props.static}
                rowHeight={props.rowHeight}
            >
                <BlockComponent
                    sizeX={props.blockObj.sizeX}
                    sizeY={props.blockObj.sizeY}
                    blockObj={blockObj}
                    htmlId={htmlId}
                    blockRef={blockRef}
                    setShowExpandButton={setShowExpandButton}
                    outsideGrid={outsideGrid}
                    isBlockPreview={isBlockPreview}
                />
                {props.children}
            </BlockWrapper>
        </ErrorBoundary>
    );
})));

export default Block;
